<template>
  <div class="employee-profile">
    <!-- 头部操作区 -->
    <div class="profile-header">
      <button class="back-btn" @click="$emit('close')">
        <i class="el-icon-arrow-left"></i> 返回列表
      </button>
    </div>

    <!-- 基本信息模块 -->
    <div class="profile-section">
      <div class="section-header">
        <h3 class="section-title">基本信息</h3>
        <div class="action-buttons">
          <button class="edit-btn" @click="toggleEdit('basic')">
            <img src="@/assets/edit-icon.png" alt="编辑" class="edit-icon" />
          </button>
        </div>
      </div>
      <div class="info-grid">
        <div class="info-row">
          <span class="info-label">姓名：</span>
          <span
            class="info-value"
            :contenteditable="editing.basic"
            @focus="setCaret($event.target)"
          >
            {{ employee.name }}
          </span>
        </div>
        <div class="info-row">
          <span class="info-label">性别：</span>
          <span
            class="info-value"
            :contenteditable="editing.basic"
            @focus="setCaret($event.target)"
          >
            {{ employee.gender }}
          </span>
        </div>
        <div class="info-row">
          <span class="info-label">出生年月：</span>
          <span
            class="info-value"
            :contenteditable="editing.basic"
            @focus="setCaret($event.target)"
          >
            {{ employee.birth_day }}
          </span>
        </div>
        <div class="info-row">
          <span class="info-label">年龄：</span>
          <span class="info-value">{{ calculateAge(employee.birth_day) }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">毕业院校：</span>
          <span
            class="info-value"
            :contenteditable="editing.basic"
            @focus="setCaret($event.target)"
          >
            {{ employee.graduation_school }}
          </span>
        </div>
        <div class="info-row">
          <span class="info-label">最高学历：</span>
          <span
            class="info-value"
            :contenteditable="editing.basic"
            @focus="setCaret($event.target)"
          >
            {{ employee.highest_education }}
          </span>
        </div>
        <div class="info-row">
          <span class="info-label">工号：</span>
          <span class="info-value">{{ employee.number }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">部门：</span>
          <span
            class="info-value"
            :contenteditable="editing.basic"
            @focus="setCaret($event.target)"
          >
            {{ employee.department }}
          </span>
        </div>
        <div class="info-row">
          <span class="info-label">入职时间：</span>
          <span class="info-value">{{ employee.joinDate }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">工龄：</span>
          <span class="info-value">{{
            calculateWorkYears(employee.joinDate)
          }}</span>
        </div>
      </div>
      <div class="section-footer" v-if="editing.basic">
        <button class="blue-btn save-btn" @click="saveChanges('basic')">
          保存
        </button>
      </div>
    </div>

    <!-- 工作履历模块 -->
    <div class="profile-section">
      <div class="section-header">
        <h3 class="section-title">工作履历</h3>
        <div class="action-buttons">
          <button class="blue-btn" @click="downloadWorkExcel">
            <i class="el-icon-download"></i> 下载模板
          </button>
          <button class="blue-btn" @click="showImportDialog = true">
            <i class="el-icon-upload2"></i> 导入
          </button>
          <button class="edit-btn" @click="toggleEdit('work')">
            <img src="@/assets/edit-icon.png" alt="编辑" class="edit-icon" />
          </button>
        </div>
      </div>
      <div class="experience-list">
        <div
          v-for="(exp, index) in employee.workExperience"
          :key="index"
          class="experience-item"
        >
          <div
            class="exp-period"
            :contenteditable="editing.work"
            @focus="setCaret($event.target)"
          >
            {{ exp.startDate }} - {{ exp.endDate }}
          </div>
          <div class="exp-content">
            <div
              class="exp-title"
              :contenteditable="editing.work"
              @focus="setCaret($event.target)"
            >
              {{ exp.company }} · {{ exp.position }}
            </div>
            <div
              class="exp-desc"
              :contenteditable="editing.work"
              @focus="setCaret($event.target)"
            >
              {{ exp.description }}
            </div>
          </div>
        </div>
        <div class="template-list">
          <p
            v-for="(exp, index) in formattedWorkExperiences"
            :key="index"
            :contenteditable="editing.work"
            @focus="setCaret($event.target)"
          >
            {{ index + 1 }}. {{ exp.startYear }}至{{ exp.endYear }} 在{{
              exp.companyName
            }}
            任{{ exp.job }}
          </p>
        </div>
      </div>
      <div class="section-footer" v-if="editing.work">
        <button class="blue-btn save-btn" @click="saveChanges('work')">
          保存
        </button>
      </div>
    </div>

    <!-- 培训经历模块 -->
    <div class="profile-section">
      <div class="section-header">
        <h3 class="section-title">培训经历</h3>
      </div>
      <div class="training-list">
        <div
          v-for="(training, index) in employee.trainingExperience"
          :key="index"
          class="training-item"
          style="display: flex; align-items: center; margin-bottom: 10px"
        >
          <div
            class="training-index"
            style="flex: 0 0 auto; margin-right: 15px"
          >
            {{ index + 1 }}.
          </div>
          <div class="training-info" style="flex: 1">
            {{ training.startDate }} 至 {{ training.endDate }} 进行了
            {{ training.course }} 的培训。
          </div>
        </div>
      </div>
      <div class="section-footer" v-if="editing.training">
        <button class="blue-btn save-btn" @click="saveChanges('training')">
          保存
        </button>
      </div>
    </div>

    <!-- 绩效信息模块 -->
    <div class="profile-section">
      <div class="section-header">
        <h3 class="section-title">绩效信息</h3>
      </div>
      <div class="performance-list">
        <div
          v-for="(plan, index) in performancePlans"
          :key="index"
          class="performance-item"
        >
          <div class="perf-period">
            {{ index + 1 }}. {{ plan.yearMonth || "获取中..." }}
          </div>
          <div class="perf-content">
            <div class="perf-title">绩效是 {{ plan.total_score }}分。</div>
          </div>
        </div>
        <div v-if="performancePlans.length === 0" class="empty-message">
          暂无绩效信息
        </div>
      </div>
      <div class="section-footer" v-if="editing.performance">
        <button class="blue-btn save-btn" @click="saveChanges('performance')">
          保存
        </button>
      </div>
    </div>
    <!-- 导入对话框 -->
    <div v-if="showImportDialog" class="import-dialog">
      <div class="dialog-content">
        <h3>导入工作履历</h3>
        <div class="import-options">
          <label class="import-option">
            <input type="radio" v-model="importType" value="excel" /> Excel文件
          </label>
          <!--            <label class="import-option">-->
          <!--              <input type="radio" v-model="importType" value="csv"> CSV文件-->
          <!--            </label>-->
        </div>
        <div class="file-selector">
          <input
            type="file"
            ref="fileInput"
            @change="handleFileChange"
            accept=".xlsx,.csv"
            style="display: none"
          />
          <button @click="$refs.fileInput.click()" class="file-select-btn">
            选择文件
          </button>
          <span v-if="selectedFile" class="selected-file-name">{{
            selectedFile.name
          }}</span>
        </div>
        <div class="dialog-buttons">
          <button @click="importData" class="confirm-btn blue-btn">
            确认导入
          </button>
          <button @click="showImportDialog = false" class="cancel-btn">
            取消
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
// axios已在main.js中全局配置，使用this.$http代替直接导入的axios
export default {
  name: "EmployeeProfile",
  props: {
    employee: {
      type: Object,
      required: true,
      default: () => ({
        name: "",
        gender: "",
        birth_day: "",
        graduation_school: "",
        highest_education: "",
        id: "",
        department: "",
        joinDate: "",
        workExperience: [],
        trainingExperience: [],
        performance: [],
      }),
    },
  },
  data() {
    return {
      showImportDialog: false,
      importType: "excel",
      selectedFile: null,
      editing: {
        basic: false,
        work: false,
        training: false,
        performance: false,
      },
      formattedWorkExperiences: [], // 关键修正：预先声明
      formattedTrainningRecord: [],
      formattedPerformanceRecord: [],
      performancePlans: [],
      totalScore: 0,
      localEmployee: { ...this.employee }, // 创建本地副本，避免直接修改props
    };
  },
  async created() {
    // 页面创建时立即根据工号获取员工ID
    const employeeId = await this.fetchIdByNumber();
    // 页面创建时立即获取工作履历数据
    this.fetchWorkExperiences();
    // 页面创建时立即获取培训经历数据
    this.fetchTrainingRecord();
    // 页面创建时立即获取绩效信息数据
    this.fetchPerformanceRecord();
    // 页面创建时立即获取绩效计划和总分数据，传入获取到的employeeId
    this.fetchPerformancePlansAndScore(employeeId);
  },
  methods: {
    handleFileChange(event) {
      this.selectedFile = event.target.files[0];
    },
    downloadWorkExcel() {
      this.$http({
        method: "get",
        url: "/File/getWorkExcelFile", // 使用相对路径
        params: { type: "workExperience" },
        responseType: "blob",
      })
        .then((response) => {
          // 确保正确获取MIME类型
          const contentType =
            response.headers["content-type"] ||
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

          const blob = new Blob([response.data], {
            type: contentType,
          });

          // 改进文件名解析逻辑
          let filename = "workExperience.xlsx";
          const disposition = response.headers["content-disposition"];

          if (disposition) {
            // 优先处理RFC 5987编码的文件名
            const filenameMatch = disposition.match(
              /filename\*=(?:UTF-8'')?([^;]+)/i
            );
            if (filenameMatch && filenameMatch[1]) {
              filename = decodeURIComponent(filenameMatch[1]);
            } else {
              // 处理普通filename参数
              const normalMatch = disposition.match(/filename=([^;]+)/i);
              if (normalMatch && normalMatch[1]) {
                filename = normalMatch[1].replace(/['"]/g, "");
              }
            }
          }

          // 创建下载链接并触发下载
          const link = document.createElement("a");
          link.href = URL.createObjectURL(blob);
          link.download = filename;
          link.style.display = "none";

          document.body.appendChild(link);
          link.click();

          // 改进清理逻辑
          setTimeout(() => {
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
          }, 100);
        })
        .catch((error) => {
          console.error("下载失败:", error);
          // 改进错误信息显示
          let errorMsg = "下载失败";
          if (error.response) {
            // 处理Blob类型的错误响应
            if (error.response.data instanceof Blob) {
              const reader = new FileReader();
              reader.onload = () => {
                try {
                  const errorData = JSON.parse(reader.result);
                  errorMsg = errorData.message || errorMsg;
                } catch {
                  errorMsg = "服务器返回错误";
                }
              };
              reader.readAsText(error.response.data);
            } else {
              errorMsg = error.response.data?.message || errorMsg;
            }
          } else {
            errorMsg = error.message || errorMsg;
          }
          this.$message.error(errorMsg);
        });

      this.showExportMenu = false;
    },
    async fetchPerformanceRecord() {
      console.log("工号:", this.employee.number);
      if (!this.employee.number) {
        console.warn("工号不存在，无法获取绩效信息");
        return;
      }
      this.loading = true;
      try {
        // 关键修正：使用反引号而不是单引号
        const response = await this.$http.get(
          `/performance/number/${this.employee.number}`
        );
        this.performancerecord = response.data || [];
        this.formattedPerformanceRecord = this.formatPerformanceRecords(
          response.data
        );
        console.group("=== 绩效考核信息接口响应数据 ===");
        console.log("HTTP状态码:", response.status);
        console.log("响应数据:", response.data);
      } catch (error) {
        console.error("获取绩效考核信息接口失败:", error);
        this.$message.error("获取绩效考核信息失败");
      } finally {
        this.loading = false;
      }
    },
    formatPerformanceRecords(experiences) {
      if (!experiences) return [];
      return experiences.map((exp) => ({
        date: exp.date,
        rate: exp.rate,
        comments: exp.comments,
      }));
    },
    async fetchTrainingRecord() {
      console.log("工号:", this.employee.number);
      if (!this.employee.number) {
        console.warn("工号不存在，无法获取培训经历");
        return;
      }
      this.loading = true;
      try {
        // 关键修正：使用反引号而不是单引号
        const response = await this.$http.get(
          `/training/number/${this.employee.number}`
        );
        this.trainningrecord = response.data || [];
        this.formattedTrainningRecord = this.formatTrainingRecords(
          response.data
        );
        // 确保模板能正确显示数据
        this.$set(
          this.employee,
          "trainingExperience",
          this.formattedTrainningRecord
        );
        console.group("=== 培训经历接口响应数据 ===");
        console.log("响应数据:", response.data);
      } catch (error) {
        console.error("获取培训经历接口失败:", error);
        this.$message.error("获取培训经历失败");
      } finally {
        this.loading = false;
      }
    },
    formatTrainingRecords(experiences) {
      if (!experiences) return [];
      return experiences.map((exp) => ({
        startDate: exp.startDate,
        endDate: exp.endDate,
        course: exp.trainingCourse,
        organization: exp.assign,
        description: exp.courseDescription,
      }));
    },
    // 修正后的前端代码
    async fetchWorkExperiences() {
      console.log("工号:", this.employee.number);
      if (!this.employee.number) {
        console.warn("工号不存在，无法获取工作履历");
        return;
      }

      this.loading = true;
      try {
        // 关键修正：使用反引号而不是单引号
        const response = await this.$http.get(
          `/work-experience/${this.employee.number}`
        );
        this.workExperiences = response.data || [];
        this.formattedWorkExperiences = this.formatExperiences(response.data);
        console.log("响应数据:", response.data);
      } catch (error) {
        console.error("获取工作履历失败:", error);
        this.$message.error("获取工作履历失败");
      } finally {
        this.loading = false;
      }
    },
    formatExperiences(experiences) {
      if (!experiences) return [];
      return experiences.map((exp) => ({
        startYear: exp.startYear,
        endYear: exp.endYear,
        companyName: exp.companyName,
        job: exp.job,
      }));
    },
    toggleEdit(section) {
      this.editing[section] = !this.editing[section];
      if (this.editing[section]) {
        this.$nextTick(() => {
          const firstEditable = this.$el.querySelector(
            `.profile-section [contenteditable="true"]`
          );
          if (firstEditable) {
            firstEditable.focus();
            this.setCaret(firstEditable);
          }
        });
      }
    },
    setCaret(element) {
      const range = document.createRange();
      const selection = window.getSelection();
      range.selectNodeContents(element);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    },
    async saveChanges(section) {
      // 关闭编辑状态
      this.editing[section] = false;
      // 如果是保存工作履历，调用服务端API
      if (section === "work") {
        try {
          // 从DOM获取最新的工作履历内容
          const workExperienceItems = [];
          const templateListItems =
            this.$el.querySelectorAll(".template-list p");

          templateListItems.forEach((item) => {
            const text = item.textContent.trim();
            // 解析工作履历内容格式: "1. 2016-06-02至2018-06-02 在上海微鲸科技 任软件1工程师"
            const match = text.match(
              /(\d+)\.\s*([\d-]+)至([\d-]+)\s*在([^任]+)\s*任(.+)/
            );
            if (match) {
              workExperienceItems.push({
                startYear: match[2],
                endYear: match[3],
                companyName: match[4].trim(),
                job: match[5].trim(),
              });
            } else {
              console.warn("无法解析工作履历项格式:", text);
            }
          });
          // 遍历每个工作履历项，调用服务端API更新
          if (workExperienceItems.length > 0) {
            for (let i = 0; i < workExperienceItems.length; i++) {
              const expItem = workExperienceItems[i];
              const workExperience = {
                startYear: expItem.startYear,
                endYear: expItem.endYear,
                job: expItem.job,
                companyName: expItem.companyName,
                // 工号会在URL路径中传递
              };

              // 调用服务端API前输出日志
              console.log("准备更新工作履历数据:", {
                index: i,
                employeeNumber: this.employee.number,
                workExperience: workExperience,
              });

              try {
                const response = await this.$http.post(
                  `/work-experience/${this.employee.number}`,
                  workExperience
                );

                // 调用成功后输出日志
                console.log(`第${i + 1}个工作履历项更新成功:`, {
                  responseData: response.data,
                  status: response.status,
                  statusText: response.statusText,
                });
              } catch (apiError) {
                console.error(`第${i + 1}个工作履历项API调用失败:`, apiError);
              }
            }
          } else {
            console.warn("工作履历数组为空，未进行API调用");
          }

          // 显示成功提示
          this.$message.success("工作履历更新成功");

          // 重新获取最新的工作履历数据
          this.fetchWorkExperiences();
        } catch (error) {
          console.error("保存工作履历时发生异常:", error);
          // 处理不同类型的错误
          let errorMsg = "更新失败，请稍后重试";
          if (error.response) {
            // 服务器返回了错误响应
            console.error("服务器错误响应:", error.response);
            errorMsg = error.response.data || errorMsg;
          } else if (error.request) {
            // 请求已发出但没有收到响应
            console.error("网络请求错误，未收到响应:", error.request);
            errorMsg = "网络连接异常，请检查网络设置";
          } else {
            // 请求配置出错
            console.error("请求配置错误:", error.message);
            errorMsg = error.message || errorMsg;
          }
          this.$message.error(errorMsg);
        } finally {
          console.log("工作履历保存逻辑执行完毕");
        }
      }

      // 仍然向上级组件发送更新事件
      this.$emit("update:employee", { ...this.employee });
    },
    calculateAge(birthDate) {
      if (!birthDate) return "";
      const birthYear = new Date(birthDate).getFullYear();
      const currentYear = new Date().getFullYear();
      return currentYear - birthYear;
    },
    calculateWorkYears(joinDate) {
      if (!joinDate) return "";
      const joinYear = new Date(joinDate).getFullYear();
      const currentYear = new Date().getFullYear();
      return currentYear - joinYear + "年";
    },
    downloadTemplate() {
      console.log("下载模板");
    },
    async fetchIdByNumber() {
      if (!this.employee.number) {
        console.warn("工号不存在，无法获取员工ID");
        return null;
      }
      this.loading = true;
      try {
        // 使用相对路径，与项目中其他API调用保持一致
        const response = await this.$http.get(
          `/getIdByNumber/${this.employee.number}`
        );
        console.log("根据工号获取ID接口响应数据:", response);
        // 根据响应数据结构获取员工ID
        const employeeId = response.data?.data || "";
        console.group("=== 根据工号获取ID接口响应数据 ===");
        console.log("接口URL:", `/getIdByNumber/${this.employee.number}`);
        console.log("员工ID:", employeeId);

        // 直接更新本地副本中的ID
        if (employeeId) {
          this.localEmployee.id = employeeId;
          console.log("已更新localEmployee.id:", this.localEmployee.id);
        }

        return employeeId;
      } catch (error) {
        console.error("根据工号获取ID接口失败:", error);
        this.$message.error("获取员工ID失败");
        return null;
      } finally {
        this.loading = false;
      }
    },

    async fetchPerformancePlansAndScore(employeeId) {
      // 使用传入的employeeId或localEmployee.id作为参数
      const id = employeeId || this.localEmployee.id;
      if (!id) {
        console.warn("员工ID不存在，无法获取绩效计划和总分信息");
        return;
      }
      this.loading = true;
      try {
        // 使用获取到的员工ID作为参数
        const response = await this.$http.get(
          `/performUser/plans-with-score/${id}`
        );
        // const response = await this.$http.get(`/performUser/plans-with-score/${5}`);
        console.log("根据ID获取考核计划和分数的接口:", response);
        // 根据API响应结构，计划数组在response.data.data中
        const plans = response.data.data || [];
        // 为每个计划对象预先添加yearMonth属性，确保Vue能检测到后续变化
        this.performancePlans = plans.map((plan) => ({
          ...plan,
          yearMonth: "获取中...",
        }));
        this.totalScore = 0; // 由于返回数据中没有total_score字段，设置为0
        console.group("=== 绩效计划和总分接口响应数据 ===");
        console.log(
          "接口URL:",
          `/performUser/plans-with-score/${this.localEmployee.id}`
        );
        console.log("响应数据:", response.data);

        // 遍历每个绩效计划，调用performPlan接口获取详细信息
        for (const plan of this.performancePlans) {
          const performPlan = plan.perform_plan;
          if (performPlan) {
            try {
              const planResponse = await this.$http.get(
                `/performPlan/year-month/${performPlan}`
              );
              console.group(`=== 绩效计划 ${performPlan} 详细信息接口 ===`);
              console.log("接口URL:", `/performPlan/year-month/${performPlan}`);
              console.log("响应数据:", planResponse.data);
              console.groupEnd();

              // 将year和month格式化为YYYY-MM格式的字符串
              if (planResponse.data.data) {
                const { year, month } = planResponse.data.data;
                // 确保月份是两位数格式
                const formattedMonth = month.toString().padStart(2, "0");
                plan.yearMonth = `${year}-${formattedMonth}`;
              } else {
                plan.yearMonth = "未知";
              }
            } catch (planError) {
              console.error(
                `获取绩效计划 ${performPlan} 详细信息失败:`,
                planError
              );
              plan.yearMonth = "获取失败";
            }
          }
        }
      } catch (error) {
        console.error("获取绩效计划和总分接口失败:", error);
        this.$message.error("获取绩效计划和总分失败");
      } finally {
        this.loading = false;
      }
    },
    async importData() {
      if (!this.selectedFile) return;
      console.log("导入文件:", this.selectedFile.name);
      this.showImportDialog = false;

      this.uploading = true;
      this.uploadStatus = null;
      this.uploadResult = null;

      const formData = new FormData();
      formData.append("file", this.selectedFile);

      try {
        const response = await this.$http.post(
          "/importWorkExperience",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            onUploadProgress: (progressEvent) => {
              if (progressEvent.total) {
                this.uploadProgress = Math.round(
                  (progressEvent.loaded * 100) / progressEvent.total
                );
              }
            },
          }
        );

        // 上传成功
        this.uploadStatus = "success";
        this.uploadResult = {
          success: response.data,
          message: response.data
            ? "数据导入成功！"
            : "数据导入失败，请检查文件格式",
        };

        if (response.data) {
          this.$message.success("数据导入成功！");
          //this.resetComponent()
          // 可以触发父组件的刷新事件
          this.$emit("import-success");
        } else {
          this.$message.error("数据导入失败");
        }
        console.log(response.data);
      } catch (error) {
        console.error("上传失败:", error);
        this.uploadStatus = "exception";
        this.uploadResult = {
          success: false,
          message:
            "上传失败：" + (error.response?.data?.message || error.message),
        };
        this.$message.error("上传失败，请检查网络连接或服务器状态");
      } finally {
        this.uploading = false;
      }
    },
  },
};
</script>

<style scoped>
.employee-profile {
  padding: 20px;
  background: #fff;
  height: 100%;
}

.profile-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
  margin-bottom: 20px;
}

.back-btn {
  background: none;
  border: none;
  color: #409eff;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.blue-btn {
  background: #409eff;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
}

.profile-section {
  margin-top: 20px;
  border: 1px solid #eee;
  border-radius: 4px;
  padding: 15px;
  position: relative;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.section-title {
  color: #409eff;
  margin: 0;
  font-size: 16px;
}

.edit-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
}

.edit-icon {
  width: 16px;
  height: 16px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
}

.info-row {
  display: flex;
}

.info-label {
  color: #909399;
  min-width: 80px;
}

.info-value {
  color: #606266;
  padding: 2px 5px;
  border-radius: 3px;
}

[contenteditable="true"] {
  outline: 1px solid #409eff;
  background-color: #f5f7fa;
  padding: 2px 5px;
  border-radius: 3px;
  min-width: 50px;
  display: inline-block;
}

.section-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 15px;
}

.save-btn {
  padding: 8px 20px;
  border-radius: 4px;
  cursor: pointer;
}

.experience-list,
.performance-list {
  margin-top: 10px;
}

.experience-item {
  display: flex;
  padding: 10px 0;
  border-bottom: 1px solid #f5f5f5;
}

.performance-item {
  display: flex;
  padding: 10px 0;
  /* 移除绩效记录之间的分割线 */
  border-bottom: none;
}

.exp-period,
.perf-year {
  min-width: 120px;
  color: #909399;
}

.exp-content,
.perf-content {
  flex: 1;
}

.exp-title,
.perf-rating {
  font-weight: 500;
  margin-bottom: 5px;
}

.exp-desc,
.perf-comment {
  color: #909399;
  font-size: 13px;
}

.template-list {
  margin-top: 15px;
  padding: 10px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.template-list p {
  margin: 5px 0;
  color: #606266;
}
.import-dialog {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.dialog-content {
  background: white;
  padding: 25px;
  border-radius: 6px;
  width: 450px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.import-options {
  margin: 20px 0;
  display: flex;
  gap: 20px;
}

.file-selector {
  margin: 20px 0;
  display: flex;
  align-items: center;
  gap: 15px;
}

.dialog-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 25px;
}

.cancel-btn {
  padding: 10px 15px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: white;
  cursor: pointer;
}
</style>
